import type { FirebaseRepository } from '../models/FirebaseRepository'
import type { BreakoutUser } from '../models/BreakoutUser'
import { SectionAssignment } from '../models/SectionAssignment'
import { Section } from '../models/Section'
import {
  ROOM_CAN_START_MINUTES_BEFORE_SCHEDULED_AT,
  RoomState,
  RoomStateStatus,
} from '../models/RoomState'
import { SlideDeck } from '../models/SlideDeck'
import { LibraryObjectState, UserProfileRole } from '../types'
import { computed, makeObservable } from 'mobx'
import { DateTime } from 'luxon'
import { assert } from '../firestore-mobx/utils'
import { SectionState } from '../firestore/Section'
import { type SlideQuestion } from '../models/SlideQuestion'
import { type SlideModel } from '../models/SlideModel'
import { SlideType } from '../firestore/Slide/types'
import { type RoomStateAnswer } from '../models/RoomStateAnswer'
import { type StudentLibraryCubit } from '../cubits/StudentLibraryCubit'
import { type SettingsSectionPassPricing } from '../models/SettingsSectionPassPricing'
import { getMinGroupSizeIssue } from '../sources-of-truth/getMinGroupSizeIssue'

export class ValidLibraryObject {
  repository: FirebaseRepository
  user: BreakoutUser
  private cubitData:
    | {
        cubit: StudentLibraryCubit
        sectionId: string
        assignmentId: string
        roomId?: string //not always a room associated with an assignment
      }
    | {
        section: Section
        assignment: SectionAssignment
        roomState: RoomState
        slideDeck: SlideDeck
        sectionPassPricing: SettingsSectionPassPricing
        quizData?: {
          slideQuestions: SlideQuestion[]
          userAnswers: RoomStateAnswer[]
        }
        /** Needed to identify the End-of-Session slide's quiz questions. */
        slides?: SlideModel[]
        /**  */
        hasGrading: null | boolean
      }

  private _slideDeckAccessGrantedViaApi?: boolean

  constructor(
    params:
      | {
          repository: FirebaseRepository
          section: Section
          assignment: SectionAssignment
          roomState: RoomState
          slideDeck: SlideDeck
          quizData?: {
            slideQuestions: SlideQuestion[]
            userAnswers: RoomStateAnswer[]
          }
          slides?: SlideModel[]
          sectionPassPricing: SettingsSectionPassPricing
          hasGrading: null | boolean
          slideDeckAccessGrantedViaApi?: boolean
        }
      | {
          cubit: StudentLibraryCubit
          assignmentId: string
          sectionId: string
          roomId?: string
        }
  ) {
    makeObservable(this)
    const user =
      'cubit' in params
        ? params.cubit.repository.breakoutUser
        : params.repository.breakoutUser
    const repository =
      'cubit' in params ? params.cubit.repository : params.repository
    assert(user, 'User must be logged in')
    this.user = user
    this.repository = repository
    this.cubitData = params
    if ('slideDeckAccessGrantedViaApi' in params) {
      this._slideDeckAccessGrantedViaApi = params.slideDeckAccessGrantedViaApi
    }
  }

  @computed
  get libraryObjectId() {
    // if there is a roomState, use the roomState id
    if (this.roomState.id) return this.assignment.id + '-' + this.roomState.id
    return this.assignment.id
  }

  @computed
  get slideDeckId() {
    return this.slideDeck.id
  }

  @computed
  get slideDeckName() {
    return this.slideDeck.data.slideDeckName
  }

  @computed
  get slideDeckPrice() {
    return this.slideDeck.data.slideDeckPrice
  }

  @computed
  get slideDeckImageURL() {
    return this.slideDeck.data.slideDeckImageURL
  }

  @computed
  get slideDeckTeaser() {
    return this.slideDeck.data.slideDeckTeaser
  }

  /**
   * Whether the assignment is expired for the *current* user, honoring any
   * individual deadline extension they have. Use this instead of
   * assignment.isExpired anywhere that gates a specific student's access.
   */
  @computed
  get expiredForCurrentUser() {
    return this.assignment.isExpiredForUser(this.user.uid)
  }

  @computed
  get assignedAtIsInFutureForCurrentUser() {
    return this.assignment.assignedAtIsInFutureForUser(this.user.uid)
  }

  /**
   * The due date shown to the *current* user: their individual deadline when
   * they have one, otherwise the assignment-level due date. Student-facing
   * lists must use this so an extended student sees the date they're actually
   * held to.
   */
  @computed
  get effectiveDueDateForCurrentUser() {
    return this.assignment.getEffectiveDueDate(this.user.uid)
  }

  /**
   * Section completion blocks the current user — unless they hold an
   * individual deadline still in the future, in which case their access
   * legitimately outlives the section (individual-deadlines feature).
   */
  @computed
  get sectionCompletedForCurrentUser() {
    return (
      this.section.data.sectionState === SectionState.completed &&
      !this.assignment.hasFutureIndividualDeadline(this.user.uid)
    )
  }

  @computed
  get requiresPayment() {
    if (
      // Test sections bypass all payment/paywall flows (BL-4187)
      this.section.isTest ||
      this.expiredForCurrentUser ||
      this.sectionCompletedForCurrentUser ||
      // Free access window active — no payment required until it passes (BL-4581)
      this.section.hasActiveFreeAccessWindow ||
      (this.section.usesCodelessAccess &&
        this.roomStateStatus === RoomStateStatus.completed) ||
      (this.section.usesSectionPass &&
        this.user.sectionPassSectionIds.has(this.section.id))
    ) {
      return false
    }
    return !this.hasAccessToSlideDeckContents
  }

  /**
   * we want to return undefined if the roomState is empty or if the roomStateStatus is mustSchedule
   * to drive the student assignment UI without explicitly checking roomStateStatus everywhere
   */
  @computed
  get scheduledAtDate() {
    if (
      this.roomState.isEmpty ||
      this.roomStateStatus === RoomStateStatus.mustScheduleSuspended
    )
      return undefined
    return this.roomState.scheduledAtDate
  }

  /**
   * The priority of room > section > feature flags is observed by the broadcast server
   * if changed here the trigger must also be updated with the new priority
   */
  @computed
  get videoMethod() {
    const { videoMethod: roomVideoMethod } = this.roomState.data
    const { videoMethod: sectionVideoMethod } = this.section.data
    return (
      roomVideoMethod ??
      sectionVideoMethod ??
      this.repository.featureFlags.data.videoMethod
    )
  }

  /**
   * Whether the current user is held to the section's payment rules. Mirrors
   * the server's `isStudent` in getSlideDeckForAssignment: anyone enrolled in
   * the section's userIds pays like a participant, whatever their profile role,
   * unless they own or collaborate on the section or are admin/corre. A TA who
   * is a participant in a paid section therefore sees the paywall (BL-4931).
   */
  @computed
  get paysAsParticipant() {
    if (this.user.role === UserProfileRole.student) return true
    // isCorre covers admins too
    if (this.user.isCorre) return false
    const { uid } = this.user
    const { userIds, instructorUserId, collaboratorRWUserIds } =
      this.section.data
    if (instructorUserId === uid) return false
    if ((collaboratorRWUserIds ?? []).includes(uid)) return false
    return userIds.includes(uid)
  }

  @computed
  get hasAccessToSlideDeckContents() {
    // Test sections bypass all payment flows, so contents are open (BL-4187)
    if (this.section.isTest) return true

    // If the server-side API has already granted access, trust that
    if (this._slideDeckAccessGrantedViaApi) return true

    // Section pass grants access
    if (
      this.section.usesSectionPass &&
      this.user.sectionPassSectionIds.has(this.section.id)
    )
      return true

    const matchingTokensCount = this.user.tokens
      .filter((tokenModel) => {
        const token = tokenModel.data
        return (
          (token.consumed === true &&
            token.sectionId !== null &&
            token.assignmentId !== null &&
            token.sectionId === this.section.id &&
            token.assignmentId === this.assignment.id) ||
          (this.roomState.isNotEmpty &&
            token.roomId !== null &&
            token.roomId === this.roomState.id)
        )
      })
      .reduce((previousValue, element) => {
        return previousValue + element.data.tokenQuantity
      }, 0)

    const havePurchaseRecord = this.user.purchases.some(
      (purchase) => purchase.id === this.slideDeck.id
    )

    const result =
      (matchingTokensCount < this.slideDeck.data.slideDeckPrice ||
        !havePurchaseRecord) &&
      this.paysAsParticipant &&
      !this.slideDeck.data.slideDeckFree

    return !result
  }

  @computed
  get roomStateStatus() {
    return this.roomState.getRoomStateStatus(
      // honor the current user's individual deadline so an extended student sees
      // their room as still live while non-extended members see it expired
      this.assignment.getEffectiveExpiresAt(this.user.uid),
      this.section.data.sectionState,
      this.slideDeck.data.slideDeckSlideCount,
      this.assignment.data.assignmentState,
      this._roomHasGrade,
      // a future individual deadline outlives section completion
      this.assignment.hasFutureIndividualDeadline(this.user.uid)
    )
  }

  @computed
  get beforeSession() {
    return (
      this.libraryObjectState === LibraryObjectState.enrolled ||
      this.libraryObjectState === LibraryObjectState.invited ||
      this.libraryObjectState === LibraryObjectState.mustSchedule ||
      this.libraryObjectState === LibraryObjectState.pending ||
      this.libraryObjectState === LibraryObjectState.scheduled
    )
  }

  @computed
  get afterSession() {
    return (
      this.libraryObjectState === LibraryObjectState.completed ||
      this.libraryObjectState === LibraryObjectState.expired
    )
  }

  @computed
  get groupLeader() {
    return this.roomState.groupLeaderUserIds.includes(this.user.uid)
  }

  @computed
  get instructor() {
    return this.section.instructor
  }

  @computed
  get libraryObjectState(): LibraryObjectState {
    // reason doesn't matter here, we just need to know if we can schedule
    const canSchedule = this.canScheduleRoom

    const expired =
      this.sectionCompletedForCurrentUser || this.expiredForCurrentUser

    const cancelled = this.roomStateStatus === RoomStateStatus.canceled

    const mustSchedule = [
      RoomStateStatus.mustSchedule,
      RoomStateStatus.mustScheduleSuspended,
    ].includes(this.roomStateStatus)

    // NO ROOM
    if (!this.roomState.id) {
      if (cancelled) return LibraryObjectState.canceled
      if (expired) return LibraryObjectState.expired
      if (this.requiresPayment) return LibraryObjectState.invited
      return LibraryObjectState.enrolled
    }

    // WITH ROOM (did not start)
    if (!this.roomState.didStart) {
      if (cancelled) return LibraryObjectState.canceled
      if (expired) return LibraryObjectState.expired
      if (this.requiresPayment) return LibraryObjectState.invited
      if (this.roomState.isScheduled && !mustSchedule) {
        if (this.canStart) return LibraryObjectState.readyToStart
        return LibraryObjectState.scheduled
      }

      return canSchedule
        ? LibraryObjectState.mustSchedule
        : LibraryObjectState.pending
    }

    // WITH ROOM (did start)
    if (mustSchedule) {
      return canSchedule
        ? LibraryObjectState.mustSchedule
        : LibraryObjectState.pending
    }

    if (this.roomStateStatus === RoomStateStatus.completed) {
      return LibraryObjectState.completed
    }
    if (this.roomStateStatus === RoomStateStatus.suspended) {
      if (!this.roomState.canStart) return LibraryObjectState.scheduled
      return LibraryObjectState.suspended
    }
    if (cancelled) return LibraryObjectState.canceled
    if (expired) return LibraryObjectState.expired
    if (this.requiresPayment) return LibraryObjectState.invited
    return LibraryObjectState.live
  }

  /**
   * Minimum group size issue (if any) that should prevent the current user from
   * starting the session — e.g. the assignment requires a minimum group size that
   * the room has not yet met. Returns null when the minimum group size is satisfied
   * (or no minimum is enforced).
   */
  @computed
  get minGroupSizeIssueForRoomState() {
    return getMinGroupSizeIssue({
      roomState: this.roomState,
      minGroupSize: this.assignment.minGroupSize,
    })
  }

  @computed
  get currentUserCanStartRoom() {
    return (
      this.libraryObjectState === LibraryObjectState.readyToStart ||
      (this.libraryObjectState === LibraryObjectState.live &&
        !this.roomState.didStart)
    )
  }

  @computed
  get currentUserCanJoinRoom() {
    return (
      this.libraryObjectState === LibraryObjectState.live &&
      this.roomState.didStart
    )
  }

  @computed
  get roomCanStartTime() {
    const scheduledAt = this.roomState.data.scheduledAt
    if (!scheduledAt) return null
    return DateTime.fromJSDate(scheduledAt).minus({
      minutes: ROOM_CAN_START_MINUTES_BEFORE_SCHEDULED_AT,
    })
  }

  @computed
  get canStart() {
    const scheduledAt = this.roomState.data.scheduledAt
    const roomCanStartTime = this.roomCanStartTime
    if (!scheduledAt || !roomCanStartTime) return false

    return this.repository.currentMinute > roomCanStartTime
  }

  @computed
  // Returns 10 minutes before meeting is scheduled to start, ex: 9/12/24 12:50pm PT
  get canJoinTimeFormatted() {
    const roomCanStartTime = this.roomCanStartTime
    if (!roomCanStartTime) return ''

    return roomCanStartTime.toFormat('M/d/yy hh:mma ZZZZ')
  }

  @computed
  get quizDataRequiredForActionState() {
    // A room sitting on the End-of-Session slide reads as `completed`, so the
    // pre-work branch below excludes it. Its quiz data is still needed to tell
    // whether to offer "Finish Quiz" — but only while the window is open, which
    // keeps the extra fetch off the library's common path.
    if (this.endOfSessionWindowIsOpen) return true

    // Solo: the 1-person room is created lazily, so the pre-work quiz gate
    // applies before any room exists or is scheduled. Instructor-led solo
    // (no room yet, creation forbidden) is excluded — the quiz can't be
    // taken until the instructor creates the room.
    if (
      this.assignment.isSolo &&
      (this.roomState.isNotEmpty ||
        !this.assignment.data.forbidStudentGroupCreation) &&
      !(this.roomStateStatus === RoomStateStatus.completed) &&
      !this.expiredForCurrentUser &&
      !this.sectionCompletedForCurrentUser &&
      this.hasAccessToSlideDeckContents &&
      !this.requiresPayment
    )
      return true

    // if not completed but is scheduled, we must check if we need to complete the quiz
    return (
      this.roomState.isNotEmpty &&
      this.roomState.isScheduled &&
      !(this.roomStateStatus === RoomStateStatus.completed) &&
      !this.expiredForCurrentUser &&
      !this.sectionCompletedForCurrentUser &&
      this.hasAccessToSlideDeckContents &&
      !this.requiresPayment
    )
  }

  @computed
  get currentUserWasAbsentAndCanRejoin(): boolean {
    const absentUserIds = this.roomState.data.absentUserIds ?? []
    if (!absentUserIds.includes(this.user.uid)) return false
    // Per-student gating: honor this user's individual deadline / section
    // completion (not the assignment-level isExpired) so an extended student
    // can still rejoin.
    if (this.expiredForCurrentUser) return false
    if (this.sectionCompletedForCurrentUser) return false
    // solo rejoins their own room; the group join/leave flags don't apply
    // (instructor-led solo forces both true)
    if (this.assignment.isSolo) return true
    if (this.assignment.forbidStudentGroupJoining) return false
    if (this.assignment.forbidStudentGroupLeaving) return false
    return true
  }

  getActionState({
    hasOutstandingPeerReview,
  }: {
    hasOutstandingPeerReview: boolean
  }): ValidLibraryObjectActionState {
    if (
      this.assignedAtIsInFutureForCurrentUser &&
      this.libraryObjectState !== LibraryObjectState.canceled
    )
      return ValidLibraryObjectActionState.availableOn

    if (
      this.quizDataRequiredForActionState &&
      this.libraryObjectState !== LibraryObjectState.completed &&
      this.libraryObjectState !== LibraryObjectState.expired &&
      this.libraryObjectState !== LibraryObjectState.invited &&
      this.libraryObjectState !== LibraryObjectState.canceled
    ) {
      if (this.quizData === undefined) {
        return ValidLibraryObjectActionState.loading
      }
      // check if we have answers for all the pre-work quiz questions
      const preMeetingQuizQuestions = this.quizData.slideQuestions.filter(
        (q) => q.isPreMeetingQuestion
      )
      if (
        !preMeetingQuizQuestions.every((question) =>
          this.quizData!.userAnswers.some(
            (answer) => answer.data.slideQuestionId === question.id
          )
        )
      ) {
        return ValidLibraryObjectActionState.completeQuiz
      }
    }

    switch (this.libraryObjectState) {
      case LibraryObjectState.live:
        // if room is not active (but live), we can start it
        if (this.currentUserCanStartRoom) {
          // do not offer to start the session if the group size requirements
          // (e.g. minimum group size) are not yet met; solo rooms have
          // exactly one participant, so the gate never applies
          if (!this.assignment.isSolo && this.minGroupSizeIssueForRoomState)
            return ValidLibraryObjectActionState.sessionScheduled
          return ValidLibraryObjectActionState.startSession
        }
        return ValidLibraryObjectActionState.joinSession
      case LibraryObjectState.readyToStart: {
        if (this.currentUserCanStartRoom) {
          // do not offer to start the session if the group size requirements
          // (e.g. minimum group size) are not yet met; solo rooms have
          // exactly one participant, so the gate never applies
          if (!this.assignment.isSolo && this.minGroupSizeIssueForRoomState)
            return ValidLibraryObjectActionState.sessionScheduled
          return ValidLibraryObjectActionState.startSession
        }
        return ValidLibraryObjectActionState.sessionScheduled
      }
      case LibraryObjectState.invited:
        return ValidLibraryObjectActionState.enroll
      case LibraryObjectState.enrolled: {
        if (this.assignment.isSolo) {
          // instructor-led solo: the instructor places each participant into
          // their own room
          if (this.assignment.data.forbidStudentGroupCreation)
            return ValidLibraryObjectActionState.pendingGrouping
          return ValidLibraryObjectActionState.startSession
        }
        if (this.assignment.forbidStudentGroupJoining)
          return ValidLibraryObjectActionState.pendingGrouping
        return ValidLibraryObjectActionState.joinGroup
      }
      case LibraryObjectState.mustSchedule:
        // solo sessions need no scheduling — starting fills scheduledAt
        if (this.assignment.isSolo)
          return ValidLibraryObjectActionState.startSession
        return ValidLibraryObjectActionState.scheduleSession
      case LibraryObjectState.scheduled:
        // solo is self-paced, except an instructor-set time the participant
        // can't edit is honored (readyToStart opens the gate 10 min before)
        if (
          this.assignment.isSolo &&
          this.assignment.data.forbidStudentGroupScheduling !== true
        )
          return ValidLibraryObjectActionState.startSession
        return ValidLibraryObjectActionState.sessionScheduled
      case LibraryObjectState.pending:
        if (this.assignment.isSolo)
          return ValidLibraryObjectActionState.startSession
        return ValidLibraryObjectActionState.pendingScheduling
      case LibraryObjectState.completed:
        if (this.currentUserWasAbsentAndCanRejoin) {
          // solo: rejoin their own room, never a new group
          if (this.assignment.isSolo)
            return this.roomState.didStart
              ? ValidLibraryObjectActionState.joinSession
              : ValidLibraryObjectActionState.startSession
          return ValidLibraryObjectActionState.joinNewGroup
        }
        // Finishing the quiz outranks peer review: the window is short and the
        // quiz feeds the results the review is written against.
        if (this.canFinishEndOfSessionQuiz) {
          return ValidLibraryObjectActionState.finishEndOfSessionQuiz
        }
        if (
          hasOutstandingPeerReview &&
          !this.sectionCompletedForCurrentUser &&
          !this.expiredForCurrentUser
        ) {
          return ValidLibraryObjectActionState.completePeerReviews
        }
        return ValidLibraryObjectActionState.viewResults
      case LibraryObjectState.expired:
        return ValidLibraryObjectActionState.experienceExpired
      case LibraryObjectState.suspended:
        return ValidLibraryObjectActionState.suspended
      case LibraryObjectState.canceled:
        return ValidLibraryObjectActionState.canceled
    }
  }

  /**
   * The slides of the assignment's deck, when they have been loaded. The
   * library only requests them for rooms inside the End-of-Session window (see
   * `endOfSessionWindowIsOpen`), since nothing else needs them, so treat an
   * empty list as "not known yet" rather than "this deck has no slides".
   */
  @computed
  get slides(): SlideModel[] {
    if ('cubit' in this.cubitData) {
      const { cubit } = this.cubitData
      const slides = cubit.slidesBySlideDeckId.get(
        this.roomState.data.slideDeckId
      )
      if (!slides || !slides.isLoaded) return []
      return slides.models
    }
    return this.cubitData.slides ?? []
  }

  /**
   * The room is parked on the End-of-Session slide and that slide changed
   * recently enough that coming back is still meaningful. Shared by the
   * "Finish Quiz" affordance and by the gate that decides whether to fetch the
   * quiz data needed to evaluate it, so the two can never disagree.
   */
  @computed
  get endOfSessionWindowIsOpen(): boolean {
    if (!this.roomState.didStart) return false

    const activeSlide = this.roomState.data.activeSlide
    const slideCount = this.slideDeck.data.slideDeckSlideCount
    const onLastSlide =
      typeof activeSlide === 'number' &&
      slideCount > 0 &&
      activeSlide >= slideCount - 1
    if (!onLastSlide) return false

    if (!this.roomState.activeSlideChangedWithinLast30Minutes) return false

    // Note: on the last slide roomStateStatus short-circuits to `completed`, so
    // it never reflects expiry/cancellation here — check the sources directly.
    if (this.assignment.isExpired) return false
    if (this.assignment.isCancelled) return false
    if (this.section.data.sectionState === SectionState.completed) return false

    return true
  }

  /**
   * Whether the current user still owes answers to the End-of-Session quiz.
   *
   * Quiz questions live on several slide types (solo, interactive, and
   * End-of-Session), and a question only records its `slideId`, so the
   * End-of-Session slide has to be identified from the deck's slides before its
   * questions can be picked out. Returns `false` whenever the data needed to
   * decide is missing — an unanswered question is the exceptional case, and
   * guessing "unfinished" would flash the wrong call-to-action while loading.
   */
  @computed
  get currentUserHasUnfinishedEndOfSessionQuiz(): boolean {
    const quizData = this.quizData
    if (!quizData) return false

    const endOfSessionSlide = this.slides.find(
      (slide) => slide.data.slideType === SlideType.endOfSession
    )
    if (!endOfSessionSlide) return false

    const questions = quizData.slideQuestions.filter(
      (question) =>
        question.data.slideId === endOfSessionSlide.id && question.isQuiz
    )
    if (questions.length === 0) return false

    return !questions.every((question) =>
      quizData.userAnswers.some(
        (answer) =>
          answer.data.slideQuestionId === question.id && answer.data.submitted
      )
    )
  }

  /**
   * Whether to offer the "Finish Quiz" affordance. The final (End-of-Session)
   * slide is a group experience — quiz, then reconvene, processing and results
   * — so a student who stepped out before answering can come back for a short
   * window and finish. Requires the window to still be open AND the student to
   * actually owe answers; once the quiz is done there is nothing to come back
   * for and the ordinary results actions apply.
   */
  @computed
  get canFinishEndOfSessionQuiz(): boolean {
    if (!this.endOfSessionWindowIsOpen) return false
    return this.currentUserHasUnfinishedEndOfSessionQuiz
  }

  @computed
  private get canScheduleRoom(): boolean {
    // if scheduling is not strictly forbidden, we can schedule
    return !this.assignment.data.forbidStudentGroupScheduling
  }

  @computed
  get sectionPassPriceCents() {
    const { sectionPassPrice: priceFromSection } = this.section.data
    if (priceFromSection) return priceFromSection
    if ('cubit' in this.cubitData)
      return this.cubitData.cubit.sectionPassPricing.sectionPassPriceInt
    return this.cubitData.sectionPassPricing.sectionPassPriceInt
  }

  @computed
  get sectionPassPriceDollars() {
    if (!this.sectionPassPriceCents) return ''
    return (this.sectionPassPriceCents / 100).toFixed(2)
  }

  @computed
  get quizData() {
    if ('cubit' in this.cubitData) {
      const { cubit } = this.cubitData
      // room-less solo: questions are keyed by the assignment's deck and the
      // answer set is genuinely empty, not still loading
      const slideDeckId = this.roomState.isNotEmpty
        ? this.roomState.data.slideDeckId
        : this.assignment.data.slideDeckId
      const slideQuestions = cubit.slideQuestionsBySlideDeckId.get(slideDeckId)
      if (!slideQuestions || !slideQuestions.isLoaded) return undefined
      if (this.roomState.isEmpty) {
        return { slideQuestions: slideQuestions.models, userAnswers: [] }
      }
      const userAnswers = cubit.userAnswersByRoomId.get(this.roomState.id)
      if (!userAnswers || !userAnswers.isLoaded) return undefined
      return {
        slideQuestions: slideQuestions.models,
        userAnswers: userAnswers.models,
      }
    }
    return this.cubitData.quizData
  }

  @computed
  get section() {
    if ('cubit' in this.cubitData) {
      const { cubit } = this.cubitData
      const section = cubit.sections.models.find(
        (s) => s.id === this.assignment.data.sectionId
      )
      return section || Section.empty(this.repository)
    }
    return this.cubitData.section
  }

  @computed
  get assignment() {
    if ('cubit' in this.cubitData) {
      const { cubit, assignmentId } = this.cubitData
      const assignment = cubit.allSectionAssignments.find(
        (a) => a.id === assignmentId
      )
      return assignment || SectionAssignment.empty(this.repository)
    }
    return this.cubitData.assignment
  }

  @computed
  get roomState() {
    if ('cubit' in this.cubitData) {
      const { cubit, roomId } = this.cubitData
      const roomState = cubit.roomStates.models.find((r) => r.id === roomId)
      return roomState || RoomState.empty(this.repository)
    }
    return this.cubitData.roomState
  }

  @computed
  get slideDeck() {
    if ('cubit' in this.cubitData) {
      const { cubit } = this.cubitData
      const slideDeck = cubit.slideDecksById.get(
        this.assignment.data.slideDeckId
      )
      return slideDeck || SlideDeck.empty(this.repository)
    }
    return this.cubitData.slideDeck
  }

  @computed
  /** returns null if unknown true/false if known */
  private get _roomHasGrade() {
    if ('cubit' in this.cubitData) {
      const { cubit } = this.cubitData
      const gradingResult = cubit.roomHasGradingResults.get(this.roomState.id)
      return gradingResult === undefined ? null : gradingResult
    }
    return this.cubitData.hasGrading
  }
}

export enum ValidLibraryObjectActionState {
  enroll = 'enroll',
  availableOn = 'availableOn',
  joinGroup = 'joinGroup',
  scheduleSession = 'scheduleSession',
  completeQuiz = 'completeQuiz',
  sessionScheduled = 'sessionScheduled',
  pendingGrouping = 'pendingGrouping',
  pendingScheduling = 'pendingScheduling',
  joinSession = 'joinSession',
  startSession = 'startSession',
  viewResults = 'viewResults',
  completePeerReviews = 'completePeerReviews',
  experienceExpired = 'experienceExpired',
  loading = 'loading',
  suspended = 'suspended',
  canceled = 'canceled',
  joinNewGroup = 'joinNewGroup',
  finishEndOfSessionQuiz = 'finishEndOfSessionQuiz',
}
